/**
 * Drawer Content Component
 *
 * Content for mobile navigation drawer.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import { cn } from '@/shared/lib';
import { useAuthStore } from '@/core/auth/auth.store';
import { useNavigationItems } from '../../hooks/use-navigation-items';

interface DrawerContentProps {
  onClose: () => void;
}

export const DrawerContent = ({ onClose }: DrawerContentProps) => {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mainItems, secondaryItems, isActive } = useNavigationItems();

  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase() || 'U'
    : 'U';

  const handleNavClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    onClose();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <h2 className="text-lg font-semibold text-neutral-900">
          {t('drawer.title')}
        </h2>
        <button
          onClick={onClose}
          className="p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* User Profile */}
      <div className="px-4 py-4 border-b border-neutral-200">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center text-white text-lg font-medium">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-base font-semibold text-neutral-900 truncate">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="text-sm text-neutral-500 truncate">
              {user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        <div className="space-y-1">
          {mainItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const label = t(item.labelKey, item.label);

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                  active
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-600 hover:bg-neutral-100'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{label}</span>
                {item.badge !== undefined && item.badge > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-white">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Secondary Items */}
        <div className="mt-6 pt-6 border-t border-neutral-200 space-y-1">
          {secondaryItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const label = t(item.labelKey, item.label);

            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.path)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-colors',
                  active
                    ? 'bg-neutral-900 text-white'
                    : 'text-neutral-600 hover:bg-neutral-100'
                )}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-medium">{label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Footer: Logout */}
      <div className="px-3 py-4 border-t border-neutral-200">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="text-sm font-medium">{t('drawer.logout')}</span>
        </button>
      </div>
    </div>
  );
};

export default DrawerContent;
